/**
 * Wizard Page
 *
 * Huvudsida för årsredovisnings-wizarden
 * Hanterar routing mellan steg och state
 *
 * K2-LÄGE (5 steg):
 * Steg 1: Bokföringsdata → välj datakälla (SIE-fil, manuell inmatning, integration)
 * Steg 2: Översikt → granska & validera
 * Steg 3: Komplettera → fyll i uppgifter
 * Steg 4: Disposition → resultatdisposition
 * Steg 5: Exportera → förhandsgranska & ladda ner
 *
 * K3-LÄGE (6 steg):
 * Steg 1: Bokföringsdata → välj datakälla
 * Steg 2: Översikt → granska & validera
 * Steg 3: Kassaflödesanalys → K3-obligatorisk (steg 2.5 i URL)
 * Steg 4: Komplettera → fyll i uppgifter (inkl. uppskjuten skatt, komponentavskrivning)
 * Steg 5: Disposition → resultatdisposition
 * Steg 6: Exportera → förhandsgranska & ladda ner
 */

import { useState, useEffect, useMemo } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { reportsApi, configApi, type AnnualReport, type Company, type ParsedSieData, type UserInput, type AccountingStandard } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { WizardLayout } from "./WizardLayout";
import { Step1DataSource } from "./Step1DataSource";
import { Step2Overview } from "./Step2Overview";
import { Step2_5CashFlow } from "./Step2_5CashFlow";
import { Step3Complete } from "./Step3Complete";
import { Step4Disposition } from "./Step4Disposition";
import { Step5Export } from "./Step5Export";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";

/**
 * Check if a report is locked (finalized or edit window expired)
 */
function isReportLocked(report: AnnualReport, editWindowDays: number): boolean {
  // Finalized reports are always locked
  if (report.status === "finalized") {
    return true;
  }

  // Only completed/exported reports can be time-locked
  if (report.status !== "completed" && report.status !== "exported") {
    return false;
  }

  // No time-based locking if edit window is 0 or negative
  if (editWindowDays <= 0) {
    return false;
  }

  // Use completedAt as anchor, fall back to updatedAt
  const lockAnchor = report.completedAt
    ? new Date(report.completedAt)
    : new Date(report.updatedAt);

  const lockDate = new Date(lockAnchor.getTime() + editWindowDays * 24 * 60 * 60 * 1000);
  return new Date() > lockDate;
}

// K2 steg (5 steg)
const K2_STEP_NAMES: Record<number, string> = {
  1: "Bokföringsdata",
  2: "Översikt",
  3: "Komplettera",
  4: "Disposition",
  5: "Exportera",
};

// K3 steg (6 steg, med kassaflöde som steg 2.5)
const K3_STEP_NAMES: Record<string, string> = {
  "1": "Bokföringsdata",
  "2": "Översikt",
  "2.5": "Kassaflödesanalys",
  "3": "Komplettera",
  "4": "Disposition",
  "5": "Exportera",
};

// K3 step order for navigation
const K3_STEP_ORDER = ["1", "2", "2.5", "3", "4", "5"];

/**
 * Get the accounting standard from the report
 */
function getAccountingStandard(report: AnnualReport | null): AccountingStandard {
  return (report?.accountingStandard as AccountingStandard) || "K2";
}

/**
 * Get step name based on accounting standard
 */
function getStepName(step: string, standard: AccountingStandard): string {
  if (standard === "K3") {
    return K3_STEP_NAMES[step] || `Steg ${step}`;
  }
  const stepNum = parseFloat(step);
  return K2_STEP_NAMES[stepNum] || `Steg ${step}`;
}

/**
 * Get total number of steps based on accounting standard
 */
function getTotalSteps(standard: AccountingStandard): number {
  return standard === "K3" ? 6 : 5;
}

/**
 * Get the display step number for K3 (converts 2.5 → 3, 3 → 4, etc.)
 */
function getDisplayStepNumber(step: string, standard: AccountingStandard): number {
  if (standard !== "K3") {
    return parseFloat(step);
  }
  const index = K3_STEP_ORDER.indexOf(step);
  return index >= 0 ? index + 1 : parseFloat(step);
}

/**
 * Get next step in wizard
 * Exported for use by step components
 */
export function getNextStep(currentStep: string, standard: AccountingStandard): string | null {
  if (standard === "K3") {
    const index = K3_STEP_ORDER.indexOf(currentStep);
    if (index >= 0 && index < K3_STEP_ORDER.length - 1) {
      return K3_STEP_ORDER[index + 1];
    }
    return null;
  }
  const stepNum = parseFloat(currentStep);
  return stepNum < 5 ? String(stepNum + 1) : null;
}

/**
 * Get previous step in wizard
 * Exported for use by step components
 */
export function getPreviousStep(currentStep: string, standard: AccountingStandard): string | null {
  if (standard === "K3") {
    const index = K3_STEP_ORDER.indexOf(currentStep);
    if (index > 0) {
      return K3_STEP_ORDER[index - 1];
    }
    return null;
  }
  const stepNum = parseFloat(currentStep);
  return stepNum > 1 ? String(stepNum - 1) : null;
}

export function WizardPage() {
  const { reportId, step } = useParams<{ reportId: string; step?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [report, setReport] = useState<AnnualReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editWindowDays, setEditWindowDays] = useState(30);

  // Check if returning from OAuth - source=integration in URL
  const sourceParam = searchParams.get("source");

  // Support both integer steps (1,2,3,4,5) and decimal steps (2.5 for K3)
  const currentStep = step || "1";

  // Load edit window config
  useEffect(() => {
    configApi.getPublic().then((res) => {
      if (res.success && res.data) {
        setEditWindowDays(res.data.editWindowDays);
      }
    }).catch(() => {});
  }, []);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Get accounting standard from report
  const accountingStandard = useMemo(() => getAccountingStandard(report), [report]);

  // Redirect locked reports to last step (export) if trying to navigate elsewhere
  useEffect(() => {
    if (report && currentStep !== "5" && isReportLocked(report, editWindowDays)) {
      navigate(`/dashboard/wizard/${reportId}/step/5`, { replace: true });
    }
  }, [report, currentStep, editWindowDays, reportId, navigate]);

  useEffect(() => {
    if (!reportId) {
      setError("Ingen årsredovisning angiven");
      setIsLoading(false);
      return;
    }

    loadReport();
  }, [reportId]);

  // Helper: Check if K2 2026 eligibility confirmation is required but not yet confirmed
  const checkRequiresK2Confirmation = (r: AnnualReport): boolean => {
    const sieData = r.parsedSieData as ParsedSieData | null;
    const userInput = r.userInput as UserInput;

    if (!sieData?.metadata?.fiscalYearStart) return false;

    const startDate = new Date(sieData.metadata.fiscalYearStart);
    const cutoffDate = new Date("2026-01-01");
    const is2026OrLater = startDate >= cutoffDate;

    const alreadyConfirmed = !!userInput?.k2Eligibility?.confirmedAt;

    return is2026OrLater && !alreadyConfirmed;
  };

  const loadReport = async () => {
    if (!reportId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await reportsApi.get(reportId);

      if (response.success && response.data) {
        const loadedReport = response.data.report;
        setReport(loadedReport);

        // Check if report is locked - redirect to step 5 (view only)
        if (isReportLocked(loadedReport, editWindowDays)) {
          if (currentStep !== "5") {
            navigate(`/dashboard/wizard/${reportId}/step/5`, { replace: true });
          }
          return;
        }

        // If K2 eligibility is required but not confirmed, always redirect to step 1
        // The K2 modal will auto-open there
        if (loadedReport.parsedSieData && checkRequiresK2Confirmation(loadedReport)) {
          if (currentStep !== "1") {
            navigate(`/dashboard/wizard/${reportId}/step/1`, { replace: true });
          }
          return;
        }

        // If returning from OAuth (source=integration), always go to step 1 with param preserved
        // This ensures Step1DataSource shows the integration view
        if (sourceParam === "integration") {
          // Navigate to step 1 with source param preserved (Step1DataSource will read it)
          if (step !== "1") {
            navigate(`/dashboard/wizard/${reportId}/step/1?source=integration`, { replace: true });
          }
          // If already on step 1, Step1DataSource will handle the source param
          return;
        }

        // Redirect to correct step if needed (normal flow)
        const savedStep = loadedReport.currentStep;
        if (!step && savedStep > 1) {
          navigate(`/dashboard/wizard/${reportId}/step/${savedStep}`, { replace: true });
        }
      } else {
        setError(response.error?.message || "Kunde inte ladda årsredovisningen");
      }
    } catch (err) {
      console.error("Load report error:", err);
      setError("Ett fel uppstod vid laddning");
      toast.error("Kunde inte ladda årsredovisningen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportUpdate = (updatedReport: AnnualReport) => {
    setReport(updatedReport);
  };

  // Handle accounting standard change (K2 <-> K3)
  const handleChangeStandard = async (newStandard: AccountingStandard) => {
    if (!report) return;

    try {
      // 1. FIRST: Reset step to 2 (backend requires step <= 2 to change standard)
      const stepResponse = await reportsApi.updateStep(report.id, 2);
      if (!stepResponse.success) {
        toast.error(stepResponse.error?.message || "Kunde inte återställa steg");
        return;
      }

      // 2. NOW update the accounting standard (will pass validation since step is 2)
      const standardResponse = await reportsApi.updateStandard(report.id, newStandard);
      if (!standardResponse.success) {
        toast.error(standardResponse.error?.message || "Kunde inte byta redovisningsstandard");
        return;
      }

      // 3. Clear K3-specific data if switching to K2
      // Note: We set accountingStandardConfirmed: true because the user already
      // confirmed the change via the "(byt)" confirmation dialog
      const currentUserInput = (report.userInput || {}) as Record<string, unknown>;
      const updatedUserInput: Record<string, unknown> = {
        ...currentUserInput,
        accountingStandardConfirmed: true,
      };

      // If switching from K3 to K2, remove K3-specific data
      if (newStandard === "K2") {
        delete updatedUserInput.cashFlowStatement;
        delete updatedUserInput.deferredTax;
        // Keep other data like company info, board members, etc.
      }

      await reportsApi.updateInput(report.id, updatedUserInput);

      // 4. Update local state directly (don't rely on API refetch which may have timing issues)
      // Construct the updated report with all changes we made
      const updatedReport: AnnualReport = {
        ...report,
        accountingStandard: newStandard,
        currentStep: 2,
        userInput: updatedUserInput,
      };

      // Use flushSync to ensure state update is processed before navigation
      flushSync(() => {
        setReport(updatedReport);
      });

      toast.success(`Bytt till ${newStandard}`);
      // Pass state to tell Step2Overview that standard was just confirmed via "(byt)" dialog
      navigate(`/dashboard/wizard/${report.id}/step/2`, {
        replace: true,
        state: { standardJustConfirmed: true }
      });
    } catch (err) {
      console.error("Failed to change accounting standard:", err);
      toast.error("Kunde inte byta redovisningsstandard");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Laddar årsredovisning...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="card max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Något gick fel</h2>
          <p className="text-gray-600 mb-6">{error || "Årsredovisningen hittades inte"}</p>
          <button
            onClick={() => navigate("/dashboard/companies")}
            className="btn btn-primary"
          >
            Tillbaka till mina företag
          </button>
        </div>
      </div>
    );
  }

  // Get company from report
  const company = report.company as Company;
  if (!company) {
    // Try to reload the report to get company info
    loadReport();
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Laddar företagsinformation...</p>
        </div>
      </div>
    );
  }

  // Navigation handler for step components
  const handleNavigateStep = (step: string | number) => {
    window.scrollTo(0, 0);
    navigate(`/dashboard/wizard/${reportId}/step/${step}`);
  };

  // Render current step based on accounting standard
  const renderStep = () => {
    const next = getNextStep(currentStep, accountingStandard);
    const prev = getPreviousStep(currentStep, accountingStandard);

    // K3 has a cash flow step between Overview and Complete
    if (accountingStandard === "K3") {
      switch (currentStep) {
        case "1":
          return <Step1DataSource report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} nextStep={next || "2"} />;
        case "2":
          return <Step2Overview report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} prevStep={prev || "1"} nextStep={next || "2.5"} />;
        case "2.5":
          return <Step2_5CashFlow report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} prevStep={prev || "2"} nextStep={next || "3"} />;
        case "3":
          return <Step3Complete report={report} company={company} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} prevStep={prev || "2.5"} nextStep={next || "4"} />;
        case "4":
          return <Step4Disposition report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} prevStep={prev || "3"} nextStep={next || "5"} />;
        case "5":
          return <Step5Export report={report} company={company} prevStep={prev || "4"} />;
        default:
          return <Step1DataSource report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} nextStep="2" />;
      }
    }

    // K2 flow (5 steps)
    switch (currentStep) {
      case "1":
        return <Step1DataSource report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} nextStep={next || "2"} />;
      case "2":
        return <Step2Overview report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} prevStep={prev || "1"} nextStep={next || "3"} />;
      case "3":
        return <Step3Complete report={report} company={company} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} prevStep={prev || "2"} nextStep={next || "4"} />;
      case "4":
        return <Step4Disposition report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} prevStep={prev || "3"} nextStep={next || "5"} />;
      case "5":
        return <Step5Export report={report} company={company} prevStep={prev || "4"} />;
      default:
        return <Step1DataSource report={report} onUpdate={handleReportUpdate} onNavigateStep={handleNavigateStep} nextStep="2" />;
    }
  };

  const stepName = getStepName(currentStep, accountingStandard);
  const displayStepNumber = getDisplayStepNumber(currentStep, accountingStandard);
  const totalSteps = getTotalSteps(accountingStandard);
  const pageMeta = DASHBOARD_PAGE_META.wizard(stepName, company?.name);

  return (
    <>
      <PageMeta
        title={pageMeta.title}
        description={pageMeta.description}
        noIndex
      />
      <WizardLayout
        company={company}
        report={report}
        currentStep={currentStep}
        displayStepNumber={displayStepNumber}
        totalSteps={totalSteps}
        accountingStandard={accountingStandard}
        onChangeStandard={handleChangeStandard}
      >
        {renderStep()}
      </WizardLayout>
    </>
  );
}
